import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseApiService } from '../../../core/api/base-api.service';
import { API_CONFIG } from '../../../core/api/api.config';
import { CQRSService, EventHistorySummary, EventHistoryItem } from '../../../core/services/cqrs.service';
import { AuthService } from '../../../core/services/auth.service';

export interface Notification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  actionUrl?: string;
  channel?: string;
  priority?: string;
  createdAt: string;
  readAt?: string;
  followUpDueAt?: string;
  followUpInterval?: string;
  followUpCount?: number;
  maxFollowUps?: number;
  actionRequired?: boolean;
  actionTakenAt?: string;
  relatedTaskId?: string;
  relatedResourceType?: string;
  relatedResourceId?: string;
}

export interface NotificationDetails {
  notification: Notification;
  followUpHistory: Array<{
    event: string;
    timestamp: Date;
    followUpCount: number;
  }>;
  actionSuggestions: string[];
  relatedNotifications: Notification[];
  resourceDetails: {
    type: string;
    id: string;
  } | null;
}

export interface NotificationPreferences {
  email: boolean;
  push: boolean;
  sms: boolean;
  types: Record<string, { email: boolean; push: boolean; sms: boolean }>;
}

@Injectable({ providedIn: 'root' })
export class NotificationsApiService extends BaseApiService {
  private readonly cqrsService = inject(CQRSService);
  private readonly auth = inject(AuthService);

  protected get baseUrl(): string {
    return API_CONFIG.getUrl(API_CONFIG.endpoints.notifications);
  }

  // ==================== CQRS COMMANDS (Write Operations) ====================

  /**
   * Mark notification as read using CQRS command
   */
  markNotificationReadCommand(
    id: string,
    metadata?: { tenantId?: string; userId?: string }
  ): Observable<{ success: boolean; data: Notification }> {
    return this.cqrsService
      .executeCommand('MarkNotificationRead', {}, id, metadata, `${this.baseUrl}/${id}/read`)
      .pipe(
        map(response => ({
          success: response.success,
          data: response.data,
        }))
      );
  }

  /**
   * Acknowledge an alert (CQRS)
   */
  acknowledgeAlert(
    id: string,
    metadata?: { tenantId?: string; userId?: string }
  ): Observable<{ success: boolean }> {
    return this.cqrsService
      .executeCommand('AcknowledgeAlert', {}, id, metadata, `${this.baseUrl}/${id}/acknowledge`)
      .pipe(
        map(response => ({
          success: response.success,
        }))
      );
  }

  /**
   * Mute or unmute a notification source (type/category)
   */
  muteSource(
    sourceKey: string,
    isMuted: boolean = true,
    metadata?: { tenantId?: string; userId?: string }
  ): Observable<{ success: boolean }> {
    return this.cqrsService
      .executeCommand(
        'MuteNotificationSource',
        { sourceKey, isMuted },
        undefined,
        metadata,
        `${this.baseUrl}/mute`
      )
      .pipe(
        map(response => ({
          success: response.success,
        }))
      );
  }

  // ==================== CQRS QUERIES (Read Operations) ====================

  /**
   * Get notifications using CQRS query
   */
  getNotificationsQuery(
    filters?: {
      read?: boolean;
      type?: string;
      limit?: number;
      offset?: number;
    },
    metadata?: { tenantId?: string; cacheKey?: string; cacheTTL?: number }
  ): Observable<{ success: boolean; data: Notification[]; total?: number }> {
    return this.cqrsService
      .executeQuery<{ data: Notification[]; total: number }>('GetNotifications', filters, undefined, metadata, this.baseUrl)
      .pipe(
        map(response => ({
          success: response.success,
          data: response.data?.data || [],
          total: response.data?.total || response.total,
        }))
      );
  }

  /**
   * Get digest summary (counts by type in a time window)
   */
  getDigest(
    params?: { from?: Date | string; to?: Date | string },
    metadata?: { tenantId?: string; cacheKey?: string; cacheTTL?: number }
  ): Observable<{ success: boolean; summary: { type: string; count: number }[] }> {
    const queryParams: any = {};
    if (params?.from) queryParams.from = params.from instanceof Date ? params.from.toISOString() : params.from;
    if (params?.to) queryParams.to = params.to instanceof Date ? params.to.toISOString() : params.to;

    return this.cqrsService
      .executeQuery<any>('GetNotificationDigest', queryParams, undefined, metadata, `${this.baseUrl}/digest`)
      .pipe(
        map(response => ({
          success: response.success,
          summary: response.data?.summary || [],
        }))
      );
  }

  /**
   * Get complete event history for a notification
   */
  /**
   * Get notification details with follow-up history and related resources
   */
  getNotificationDetails(
    notificationId: string,
    includeRelated: boolean = true,
    includeResourceDetails: boolean = true
  ): Observable<{ success: boolean; data: NotificationDetails }> {
    const user = this.auth.user();
    const userId = (user as any)?.sub || (user as any)?.id;

    const queryParams: Record<string, any> = {};
    
    // Include userId in query params (backend needs it for authorization)
    if (userId) {
      queryParams.userId = String(userId);
    }
    
    if (!includeRelated) queryParams.includeRelated = 'false';
    if (!includeResourceDetails) queryParams.includeResourceDetails = 'false';

    return this.get<{ success: boolean; data: NotificationDetails }>(
      `/${notificationId}/details`,
      queryParams
    );
  }

  getNotificationEventHistory(
    notificationId: string,
    filters?: {
      fromVersion?: number;
      toVersion?: number;
      fromDate?: Date | string;
      toDate?: Date | string;
      eventType?: string;
      performedBy?: string;
    }
  ): Observable<{ success: boolean; data: EventHistorySummary | EventHistoryItem[] }> {
    return this.cqrsService.getEventHistory(notificationId, 'Notification', filters).pipe(
      map(response => ({
        success: response.success,
        data: response.data!,
      }))
    );
  }

  getNotifications(params?: { read?: boolean; type?: string; limit?: number; offset?: number }): Observable<{ success: boolean; data: Notification[]; total?: number }> {
    const user = this.auth.user();
    const userId = (user as any)?.sub || (user as any)?.id;

    // Build query params as a plain object (BaseApiService will convert to HttpParams)
    const queryParams: Record<string, any> = {};

    if (userId) {
      queryParams.userId = String(userId);
    }

    if (params) {
      if (params.read !== undefined) {
        queryParams.isRead = params.read ? 'true' : 'false';
      }
      if (params.type) {
        queryParams.type = params.type;
      }
      if (params.limit !== undefined) {
        queryParams.limit = String(params.limit);
      }
      if (params.offset !== undefined) {
        queryParams.offset = String(params.offset);
      }
    }
    
    return this.get<{ success: boolean; data: Notification[]; total?: number }>('', queryParams);
  }

  markAsRead(id: string): Observable<{ success: boolean; data: Notification }> {
    return this.patch<{ success: boolean; data: Notification }>(`/${id}/read`, {});
  }

  markAllAsRead(): Observable<{ success: boolean; message?: string }> {
    return this.patch<{ success: boolean; message?: string }>('/read-all', {});
  }

  deleteNotification(id: string): Observable<{ success: boolean; message?: string }> {
    return this.delete<{ success: boolean; message?: string }>(`/${id}`);
  }

  getPreferences(): Observable<{ success: boolean; data: NotificationPreferences }> {
    return this.get<{ success: boolean; data: NotificationPreferences }>('/preferences');
  }

  updatePreferences(preferences: Partial<NotificationPreferences>): Observable<{ success: boolean; data: NotificationPreferences }> {
    return this.patch<{ success: boolean; data: NotificationPreferences }>('/preferences', preferences);
  }

  getSettings(): Observable<{ success: boolean; data: Record<string, any> }> {
    return this.get<{ success: boolean; data: Record<string, any> }>('/settings');
  }

  updateSettings(settings: Record<string, any>): Observable<{ success: boolean; data: Record<string, any> }> {
    return this.patch<{ success: boolean; data: Record<string, any> }>('/settings', settings);
  }
}

